import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export interface LayoutContextType {
  isLayoutVisible: boolean;
  isNavbarVisible: boolean;
  isHeaderVisible: boolean;
  isLogoVisible: boolean;
  isGradientVisible: boolean;
  isExiting: boolean;
  animationCompleted: boolean;
  backgroundColor: string | null;
  hideLayout: (callback?: () => void) => void;
  showLayout: () => void;
  hideNavbarAndHeader: (callback?: () => void, backgroundColor?: string) => void;
  showNavbarAndHeader: () => void;
  hideLogo: () => void;
  showLogo: () => void;
  hideGradient: () => void;
  showGradient: () => void;
  triggerExit: (callback: () => void, backgroundColor?: string) => void;
  setBackgroundColor: (color: string | null) => void;
}

interface LayoutProviderProps {
  children: React.ReactNode;
  hiddenRoutes?: string[];
  hiddenNavbarRoutes?: string[];
  hiddenHeaderRoutes?: string[];
  hiddenLogoRoutes?: string[];
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// Default routes where layout should be hidden
const DEFAULT_HIDDEN_ROUTES = ['/welcome', '/business-select'];

// Default routes where navbar should be hidden
const DEFAULT_HIDDEN_NAVBAR_ROUTES = ['/purchase/treasurybill', '/purchase/confirmation'];

// Default routes where header should be hidden
const DEFAULT_HIDDEN_HEADER_ROUTES = ['/purchase/treasurybill', '/purchase/confirmation'];

// Default routes where logo should be hidden
const DEFAULT_HIDDEN_LOGO_ROUTES = ['/welcome'];

export const LayoutProvider: React.FC<LayoutProviderProps> = ({
  children,
  hiddenRoutes = DEFAULT_HIDDEN_ROUTES,
  hiddenNavbarRoutes = DEFAULT_HIDDEN_NAVBAR_ROUTES,
  hiddenHeaderRoutes = DEFAULT_HIDDEN_HEADER_ROUTES,
  hiddenLogoRoutes = DEFAULT_HIDDEN_LOGO_ROUTES,
}) => {
  const [isLayoutVisible, setIsLayoutVisible] = useState(true);
  const [isNavbarVisible, setIsNavbarVisible] = useState(false); // Start hidden - users start from login
  const [isHeaderVisible, setIsHeaderVisible] = useState(false); // Start hidden - users start from login
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const [isGradientVisible, setIsGradientVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [animationCompleted, setAnimationCompleted] = useState(true); // Start as true for initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is initial load
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null);
  const location = useLocation();

  // Check if current route should hide layout
  const shouldHideLayout = useCallback(
    (pathname: string) => {
      return hiddenRoutes.some((route) => {
        // Support exact match and prefix match
        return pathname === route || pathname.startsWith(route + '/');
      });
    },
    [hiddenRoutes],
  );

  // Check if current route should hide navbar
  const shouldHideNavbar = useCallback(
    (pathname: string) => {
      return hiddenNavbarRoutes.some((route) => {
        return pathname === route || pathname.startsWith(route + '/');
      });
    },
    [hiddenNavbarRoutes],
  );

  // Check if current route should hide header
  const shouldHideHeader = useCallback(
    (pathname: string) => {
      return hiddenHeaderRoutes.some((route) => {
        return pathname === route || pathname.startsWith(route + '/');
      });
    },
    [hiddenHeaderRoutes],
  );

  // Check if current route should hide logo
  const shouldHideLogo = useCallback(
    (pathname: string) => {
      return hiddenLogoRoutes.some((route) => {
        return pathname === route || pathname.startsWith(route + '/');
      });
    },
    [hiddenLogoRoutes],
  );

  // Handle route changes - update visibility for layout, navbar, header, and logo
  useEffect(() => {
    const currentPath = location.pathname;

    // Update layout visibility
    const shouldHideLayoutNow = shouldHideLayout(currentPath);
    if (shouldHideLayoutNow && isLayoutVisible) {
      setIsLayoutVisible(false);
      setAnimationCompleted(true);
    } else if (!shouldHideLayoutNow && !isLayoutVisible) {
      setIsLayoutVisible(true);
      setIsExiting(false);
      setAnimationCompleted(false);

      const timer = setTimeout(() => {
        setAnimationCompleted(true);
      }, 800);

      return () => clearTimeout(timer);
    }

    // Update logo visibility based on routes
    setIsLogoVisible(!shouldHideLogo(currentPath));

    // For navbar and header visibility:
    // - If we're on a route that should show them AND it's not the initial load
    // - OR if we're on dashboard and it's initial load (direct navigation to dashboard)
    const shouldShowNavbar = !shouldHideNavbar(currentPath);
    const shouldShowHeader = !shouldHideHeader(currentPath);

    // Only show navbar/header on route change if:
    // 1. It's not initial load (prevent showing before animations), OR
    // 2. It's initial load and we're directly on a route that needs them (like direct dashboard access)
    if (
      !isInitialLoad ||
      currentPath === '/dashboard' ||
      currentPath === '/wallet' ||
      currentPath === '/manage'
    ) {
      setIsNavbarVisible(shouldShowNavbar);
      setIsHeaderVisible(shouldShowHeader);
    }

    // Reset background color when navigating to routes that show navbar and header (default layout)
    if (shouldShowNavbar && shouldShowHeader && !isInitialLoad) {
      setBackgroundColor(null);
    }

    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [
    location.pathname,
    isLayoutVisible,
    shouldHideLayout,
    shouldHideNavbar,
    shouldHideHeader,
    shouldHideLogo,
    isInitialLoad,
  ]);

  // Handle initial load - set layout visibility based on route, but keep navbar/header hidden
  useEffect(() => {
    const currentPath = location.pathname;

    // Set layout and logo visibility based on route
    setIsLayoutVisible(!shouldHideLayout(currentPath));
    setIsLogoVisible(!shouldHideLogo(currentPath));

    // Always start with hidden navbar/header - they'll be shown with animations when needed
    // This prevents exit animations on first navigation
    setIsNavbarVisible(false);
    setIsHeaderVisible(false);

    setAnimationCompleted(true);
    setIsInitialLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const hideLayout = useCallback(
    (callback?: () => void) => {
      if (!isLayoutVisible) {
        callback?.();
        return;
      }

      setIsExiting(true);

      // Wait for exit animation to complete
      setTimeout(() => {
        setIsLayoutVisible(false);
        setIsExiting(false);
        setAnimationCompleted(false);
        callback?.();
      }, 600); // Match the exit animation duration
    },
    [isLayoutVisible],
  );

  const showLayout = useCallback(() => {
    if (isLayoutVisible) return;

    setIsLayoutVisible(true);
    setIsExiting(false);
    setAnimationCompleted(false);

    // Wait for entrance animations to complete
    const timer = setTimeout(() => {
      setAnimationCompleted(true);
    }, 800);

    return () => clearTimeout(timer);
  }, [isLayoutVisible]);

  const hideNavbarAndHeader = useCallback((callback?: () => void, backgroundColor?: string) => {
    if (backgroundColor) {
      setBackgroundColor(backgroundColor);
    }

    setIsNavbarVisible(false);
    setIsHeaderVisible(false);
    setIsExiting(true); // This will trigger main content fade-out

    // Wait for exit animation to complete if callback provided
    if (callback) {
      setTimeout(() => {
        setIsExiting(false); // Reset exiting state
        callback();
      }, 400); // Match the exit animation duration
    }
  }, []);

  const showNavbarAndHeader = useCallback(() => {
    setIsNavbarVisible(true);
    setIsHeaderVisible(true);
    setIsExiting(false); // Ensure we're not in exiting state
    setBackgroundColor(null); // Reset background color to default
  }, []);

  const hideLogo = useCallback(() => {
    setIsLogoVisible(false);
  }, []);

  const showLogo = useCallback(() => {
    setIsLogoVisible(true);
  }, []);

  const hideGradient = useCallback(() => {
    setIsGradientVisible(false);
  }, []);

  const showGradient = useCallback(() => {
    setIsGradientVisible(true);
  }, []);

  const triggerExit = useCallback((callback: () => void, backgroundColor?: string) => {
    if (backgroundColor) {
      setBackgroundColor(backgroundColor);
    }

    setIsExiting(true);

    // Wait for exit animation to complete, then execute callback
    setTimeout(() => {
      callback();
    }, 600); // Match the exit animation duration
  }, []);

  const value: LayoutContextType = {
    isLayoutVisible,
    isNavbarVisible,
    isHeaderVisible,
    isLogoVisible,
    isGradientVisible,
    isExiting,
    animationCompleted,
    backgroundColor,
    hideLayout,
    showLayout,
    hideNavbarAndHeader,
    showNavbarAndHeader,
    hideLogo,
    showLogo,
    hideGradient,
    showGradient,
    triggerExit,
    setBackgroundColor,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

export default LayoutContext;
